import {
  Button,
  Flex,
  Heading,
  HStack,
  Skeleton,
  TabList,
  TabPanel,
  TabPanels,
} from "@chakra-ui/react";
import React, { useCallback, useEffect } from "react";
import { NavLink, useSearchParams } from "react-router-dom";
import {
  getMemberSearchQuery,
  normalizeSearchQuery,
} from "src/components/graphql/utils";
import { SearchInput } from "src/components/Inputs/SearchInput";
import { GQLRemoteDataView } from "src/components/Layout/RemoteDataView";
import { Tab, TabsWithHash } from "src/components/Layout/TabsWithHash";
import { Breadcrumb } from "src/components/Navigation/Breadcrumb";
import { WithRequiredHasuraRoles } from "src/components/Permissions/WithRequiredHasuraRoles";
import { WithUserPermissions } from "src/components/Permissions/WithUserPermissions";
import { SortType, UNSORTED } from "src/components/Table/SortButton";
import { SearchAndFilterTypes } from "src/constants";
import { usePaginationParams } from "src/hooks/useCommonSearchParams";
import { useOrganization } from "src/hooks/useOrganization";
import { usePaginatedRemoteDataQuery } from "src/hooks/usePaginatedRemoteDataQuery";
import { TeamList } from "src/scenes/orgAdmin/team/ListTeams";
import * as breadcrumb from "src/services/breadcrumb";
import * as Url from "src/services/url";
import * as GQL from "src/types/graphql";
import { HasuraRole } from "src/types/hasuraRole";
import { GET_MEMBERS, GET_TEAMS } from "./graphql/queries";
import { MemberList } from "./ListMembers";

const ARCHIVED_MEMBERS_HASH = "archived-members";
const MEMBERS_HASH = "members";
const TEAMS_HASH = "teams";

export const Team = () => {
  const organization = useOrganization();
  const [searchParams, setSearchParams] = useSearchParams();
  const { pagination: membersPagination, setPagination: setMembersPagination } =
    usePaginationParams();
  const {
    pagination: archivedMembersPagination,
    setPagination: setArchivedMembersPagination,
  } = usePaginationParams();
  const { pagination: teamsPagination, setPagination: setTeamsPagination } =
    usePaginationParams();

  const {
    usePaginatedQuery: useMembersPaginatedQuery,
    useUnpaginatedQueryPromise: useMembersUnpaginatedQueryPromise,
  } = usePaginatedRemoteDataQuery<GQL.GetMembers, GQL.GetMembersVariables>(
    GET_MEMBERS,
    {
      active: true,
      organizationId: organization.map((org) => org.id).withDefault(""),
      limit: membersPagination.limit,
      offset: membersPagination.offset,
      search: searchParams.has(SearchAndFilterTypes.Search)
        ? getMemberSearchQuery(
            searchParams.get(SearchAndFilterTypes.Search) ?? ""
          )
        : {},
    }
  );

  const {
    usePaginatedQuery: useArchivedMembersPaginatedQuery,
    useUnpaginatedQueryPromise: useArchivedMembersUnpaginatedQueryPromise,
  } = usePaginatedRemoteDataQuery<GQL.GetMembers, GQL.GetMembersVariables>(
    GET_MEMBERS,
    {
      active: false,
      organizationId: organization.map((org) => org.id).withDefault(""),
      limit: archivedMembersPagination.limit,
      offset: archivedMembersPagination.offset,
      search: searchParams.has(SearchAndFilterTypes.Search)
        ? getMemberSearchQuery(
            searchParams.get(SearchAndFilterTypes.Search) ?? ""
          )
        : {},
    }
  );

  const {
    usePaginatedQuery: useTeamsPaginatedQuery,
    useUnpaginatedQueryPromise: useTeamsUnpaginatedQueryPromise,
  } = usePaginatedRemoteDataQuery<GQL.GetTeams, GQL.GetTeamsVariables>(
    GET_TEAMS,
    {
      organizationId: organization.map((org) => org.id).withDefault(""),
      orderBy: [
        {
          created_at: GQL.order_by.desc,
        },
      ],
      limit: teamsPagination.limit,
      offset: teamsPagination.offset,
    }
  );

  const { remoteData: remoteDataMembers, refetch: refetchMembers } =
    useMembersPaginatedQuery({
      skip: !organization.hasData(),
      notifyOnNetworkStatusChange: true,
      fetchPolicy: "no-cache",
    });

  const {
    remoteData: remoteDataArchivedMembers,
    refetch: refetchArchivedMembers,
  } = useArchivedMembersPaginatedQuery({
    skip: !organization.hasData(),
    notifyOnNetworkStatusChange: true,
    fetchPolicy: "no-cache",
  });

  const { remoteData: remoteDataTeams, refetch: refetchTeams } =
    useTeamsPaginatedQuery({
      skip: !organization.hasData(),
      notifyOnNetworkStatusChange: true,
      fetchPolicy: "no-cache",
    });

  const fetchAllTeamsPromise = useTeamsUnpaginatedQueryPromise();
  const fetchAllMembersPromise = useMembersUnpaginatedQueryPromise();
  const fetchAllArchivedMembersPromise =
    useArchivedMembersUnpaginatedQueryPromise();

  const handleMembersSort = useCallback(
    (columnName: string, sortType: SortType) => {
      const sort = sortType === UNSORTED ? null : (sortType as GQL.order_by);

      if (columnName === "role") {
        refetchMembers({ orderBy: [{ user_group: { name: sort } }] });
      } else {
        refetchMembers({ orderBy: [{ [columnName]: sort }] });
      }
    },
    [refetchMembers]
  );

  const handleArchivedMembersSort = useCallback(
    (columnName: string, sortType: SortType) => {
      const sort = sortType === UNSORTED ? null : (sortType as GQL.order_by);

      if (columnName === "role") {
        refetchArchivedMembers({ orderBy: [{ user_group: { name: sort } }] });
      } else {
        refetchArchivedMembers({ orderBy: [{ [columnName]: sort }] });
      }
    },
    [refetchArchivedMembers]
  );

  const handleTeamsSort = useCallback(
    (columnName: string, sortType: SortType) => {
      const sort = sortType === UNSORTED ? null : (sortType as GQL.order_by);

      refetchTeams({ orderBy: [{ [columnName]: sort }] });
    },
    [refetchTeams]
  );

  const handleFetchAllTeams = async () => {
    const result = await fetchAllTeamsPromise();
    if (!result.data) throw new Error("Failed to fetch teams");
    return result.data.team;
  };

  const handleFetchAllMembers = async () => {
    const result = await fetchAllMembersPromise();
    if (!result.data) throw new Error("Failed to fetch members");
    return result.data.person;
  };

  const handleFetchAllArchivedMembers = async () => {
    const result = await fetchAllArchivedMembersPromise();
    if (!result.data) throw new Error("Failed to fetch archived members");
    return result.data.person;
  };

  const handleMemberSearch = useCallback(
    (searchTerm: string) => {
      const normalized = normalizeSearchQuery(searchTerm);
      if (!normalized) {
        searchParams.delete(SearchAndFilterTypes.Search);
      } else {
        searchParams.set(SearchAndFilterTypes.Search, normalized);
      }
      searchParams.delete(SearchAndFilterTypes.Offset);
      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams]
  );

  const handleClearSearch = useCallback(
    () => handleMemberSearch(""),
    [handleMemberSearch]
  );

  const hasArchivedMembers = React.useMemo(() => {
    if (!remoteDataArchivedMembers.hasData()) {
      return false;
    }
    if (!remoteDataArchivedMembers.data.person_aggregate.totals) {
      return false;
    }

    return remoteDataArchivedMembers.data.person_aggregate.totals.total > 0;
  }, [remoteDataArchivedMembers]);

  useEffect(() => {
    if (
      remoteDataArchivedMembers.hasData() &&
      remoteDataArchivedMembers.data.person_aggregate.totals?.total === 0
    ) {
      window.location.hash = `#${MEMBERS_HASH}`;
    }
  }, [hasArchivedMembers, remoteDataArchivedMembers]);

  return (
    <Flex direction="column" rowGap={6}>
      <Breadcrumb items={breadcrumb.team.getBreadcrumbsForList(organization)} />
      <HStack justifyContent="space-between">
        <Heading as="h1" variant="admin">
          Teams & members
        </Heading>

        <HStack justifyContent="flex-end">
          <WithRequiredHasuraRoles
            roles={[HasuraRole.ORG_ADMIN, HasuraRole.ADMIN]}
          >
            <WithUserPermissions
              permissions={["team:create"]}
              loadingIndicator={<Loading />}
            >
              <Button
                colorScheme="primary"
                variant="outline"
                as={NavLink}
                to={organization
                  .map((org) => Url.OrgAdmin.Team.newTeam(org))
                  .withDefault("#")}
                size="xl"
              >
                Create team
              </Button>
            </WithUserPermissions>
          </WithRequiredHasuraRoles>
          <WithRequiredHasuraRoles
            roles={[HasuraRole.ORG_ADMIN, HasuraRole.ADMIN]}
          >
            <WithUserPermissions
              permissions={["user:create"]}
              loadingIndicator={<Loading />}
            >
              <Button
                colorScheme="primary"
                as={NavLink}
                to={organization
                  .map((org) => Url.OrgAdmin.Team.newMember(org))
                  .withDefault("#")}
                size="xl"
              >
                Create team member
              </Button>
            </WithUserPermissions>
          </WithRequiredHasuraRoles>
        </HStack>
      </HStack>

      <TabsWithHash isLazy>
        <TabList>
          <Tab hashKey={MEMBERS_HASH} value="members">
            Members
          </Tab>
          <Tab
            hashKey={ARCHIVED_MEMBERS_HASH}
            value="archived-members"
            hidden={!hasArchivedMembers}
          >
            Archived members
          </Tab>
          <WithRequiredHasuraRoles
            roles={[HasuraRole.ADMIN, HasuraRole.ORG_ADMIN]}
          >
            <Tab hashKey={TEAMS_HASH} value="teams">
              Teams
            </Tab>
          </WithRequiredHasuraRoles>
        </TabList>
        <TabPanels>
          <TabPanel key="members">
            <Flex direction="column" gap="6">
              <SearchInput
                onSearch={handleMemberSearch}
                value={searchParams.get(SearchAndFilterTypes.Search) ?? ""}
                onClear={handleClearSearch}
                placeholder="Search by name, ID or email address"
              />
              <MemberList
                active={true}
                remoteData={remoteDataMembers}
                limit={membersPagination.limit}
                offset={membersPagination.offset}
                onFetchMore={setMembersPagination}
                onSort={handleMembersSort}
                onFetchAll={handleFetchAllMembers}
                onClearSearch={handleClearSearch}
                refetch={refetchMembers}
              />
            </Flex>
          </TabPanel>
          <TabPanel key="archived-members">
            <Flex direction="column" gap="6">
              <SearchInput
                onSearch={handleMemberSearch}
                value={searchParams.get(SearchAndFilterTypes.Search) ?? ""}
                onClear={handleClearSearch}
                placeholder="Search by name, ID or email address"
              />
              <MemberList
                active={false}
                remoteData={remoteDataArchivedMembers}
                limit={archivedMembersPagination.limit}
                offset={archivedMembersPagination.offset}
                onFetchMore={setArchivedMembersPagination}
                onSort={handleArchivedMembersSort}
                onFetchAll={handleFetchAllArchivedMembers}
                onClearSearch={handleClearSearch}
                refetch={refetchArchivedMembers}
              />
            </Flex>
          </TabPanel>
          <TabPanel key="teams">
            <GQLRemoteDataView remoteData={remoteDataTeams}>
              {(data) => (
                <TeamList
                  teams={data.team}
                  limit={teamsPagination.limit}
                  offset={teamsPagination.offset}
                  count={data.team_aggregate.totals?.total ?? 0}
                  refetch={refetchTeams}
                  onFetchMore={setTeamsPagination}
                  onSort={handleTeamsSort}
                  onFetchAll={handleFetchAllTeams}
                />
              )}
            </GQLRemoteDataView>
          </TabPanel>
        </TabPanels>
      </TabsWithHash>
    </Flex>
  );
};

const Loading = () => {
  return <Skeleton width="10rem" height="3rem" />;
};
